/**
 * Extract dominant colors from an image using canvas.
 * Returns { primary, accent } hex colors.
 */
export async function extractColorsFromImage(imageDataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const size = 64;
      canvas.width = size;
      canvas.height = size;
      ctx.drawImage(img, 0, 0, size, size);

      const data = ctx.getImageData(0, 0, size, size).data;
      const buckets = {};

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
        if (a < 128) continue;
        // Skip near-white and near-black pixels
        const lum = 0.299 * r + 0.587 * g + 0.114 * b;
        if (lum > 230 || lum < 25) continue;

        // Quantize to reduce color space
        const qr = Math.round(r / 32) * 32;
        const qg = Math.round(g / 32) * 32;
        const qb = Math.round(b / 32) * 32;
        const key = `${qr},${qg},${qb}`;
        if (!buckets[key]) buckets[key] = { r: 0, g: 0, b: 0, count: 0 };
        buckets[key].r += r;
        buckets[key].g += g;
        buckets[key].b += b;
        buckets[key].count += 1;
      }

      const sorted = Object.values(buckets).sort((a, b) => b.count - a.count);
      if (sorted.length === 0) {
        resolve(null);
        return;
      }

      const toHex = (bucket) => {
        const r = Math.round(bucket.r / bucket.count);
        const g = Math.round(bucket.g / bucket.count);
        const b = Math.round(bucket.b / bucket.count);
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
      };

      const primary = toHex(sorted[0]);
      // Accent: pick the second most common distinct color, or darken primary
      let accent = primary;
      if (sorted.length > 1) {
        accent = toHex(sorted[1]);
      } else {
        // Darken primary by 20%
        const p = sorted[0];
        const dr = Math.round((p.r / p.count) * 0.8);
        const dg = Math.round((p.g / p.count) * 0.8);
        const db = Math.round((p.b / p.count) * 0.8);
        accent = `#${dr.toString(16).padStart(2, '0')}${dg.toString(16).padStart(2, '0')}${db.toString(16).padStart(2, '0')}`;
      }

      resolve({ primary, accent });
    };
    img.onerror = () => resolve(null);
    img.src = imageDataUrl;
  });
}
